import { Box, Chip, Stack, Tab, Tabs, TextField, Typography } from '@mui/material'
import { LegacyRef, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import AccountCard, { AdditionalAccountCardProps } from './AccountCard'
import ItemList, { ItemListProps } from './ItemList'
import Account, { AccountID } from '../../domain/model/account'
import { formatFull } from '../../domain/model/currency'
import { isOldAndUnused } from '../../domain/model/usage'
import MixedAugmentation from '../../service/MixedAugmentation'
import { matchesSearch } from '../../utils'
import { DrawerContext } from '../Menu'
import { CustomScrolling } from '../shared/CustomScrolling'
import GroupHeader from '../shared/GroupHeader'
import { Column } from '../shared/Layout'

type tabs = 'mine' | 'others'
const groupOther = 'Other'

type GroupedAccountListProps = {
  showZeroBalances?: boolean
  showGroupTotals?: boolean
  groupBy?: 'institution' | 'type'
  filterable?: boolean
  searchQuery?: string
  focusedAccount?: AccountID | null
  onFilteredAccountsChange?: (accounts: Account[]) => void
  scrollingContainerRef?: LegacyRef<HTMLDivElement> | undefined
}

type Props = ItemListProps<AccountID, Account, AdditionalAccountCardProps> & GroupedAccountListProps

export const isAccountUnused = (account: Account, balances: Map<number, number> | undefined): boolean =>
  isOldAndUnused(account.createdAt, balances?.values() ?? [])

const GroupedAccountList = (props: Props) => {
  const {
    items,
    selectConfiguration,
    additionalItemsProps,
    ItemComponent = AccountCard,

    scrollingContainerRef,
    filterable: inlineSearch = false,
    searchQuery,
    showZeroBalances = false,
    groupBy = 'institution',
    showGroupTotals = false,
    onFilteredAccountsChange,
  } = props

  const { augmentedTransactions: transactions, accountBalances, exchangeRateOnDay } = useContext(MixedAugmentation)
  const { defaultCurrency } = useContext(MixedAugmentation)
  const { privacyMode } = useContext(DrawerContext)
  const [activeTab, setActiveTab] = useState<tabs>('mine')

  const myOwnAccounts = useMemo(() => items.filter((account) => account.isMine), [items])
  const otherAccounts = useMemo(() => items.filter((account) => !account.isMine), [items])

  const [inlineFilter, setInlineFilter] = useState('')
  const filterable = inlineSearch || typeof searchQuery === 'string'
  const filter = searchQuery ?? inlineFilter

  const filterAccounts = useCallback(
    (account: Account) => {
      if (filterable && !matchesSearch(account.name, filter)) {
        return false
      }

      if (!showZeroBalances && isAccountUnused(account, accountBalances?.get(account.id))) return false

      return true
    },
    [filterable, filter, showZeroBalances, accountBalances],
  )

  const myOwnOrderedAccounts = useMemo(() => {
    const visited = new Set<number>()
    const ordered = new Array<Account>()

    for (const transaction of transactions) {
      if (transaction.senderId !== null && !visited.has(transaction.senderId)) {
        const account = myOwnAccounts.find((a) => a.id === transaction.senderId)
        if (typeof account !== 'undefined') {
          ordered.push(account)
          visited.add(transaction.senderId)
        }
      }
      if (transaction.receiverId !== null && !visited.has(transaction.receiverId)) {
        const account = myOwnAccounts.find((a) => a.id === transaction.receiverId)
        if (typeof account !== 'undefined') {
          ordered.push(account)
          visited.add(transaction.receiverId)
        }
      }
    }

    for (const account of myOwnAccounts) {
      if (!visited.has(account.id)) {
        ordered.push(account)
      }
    }

    return ordered.filter(filterAccounts)
  }, [myOwnAccounts, transactions, filterAccounts])

  const otherOrderedAccounts = useMemo(() => {
    const visited = new Set<number>()
    const ordered = new Array<Account>()

    for (const transaction of transactions) {
      if (transaction.senderId !== null && !visited.has(transaction.senderId)) {
        const account = otherAccounts.find((a) => a.id === transaction.senderId)
        if (typeof account !== 'undefined') {
          ordered.push(account)
          visited.add(transaction.senderId)
        }
      }
      if (transaction.receiverId !== null && !visited.has(transaction.receiverId)) {
        const account = otherAccounts.find((a) => a.id === transaction.receiverId)
        if (typeof account !== 'undefined') {
          ordered.push(account)
          visited.add(transaction.receiverId)
        }
      }
    }

    for (const account of otherAccounts) {
      if (!visited.has(account.id)) {
        ordered.push(account)
      }
    }

    return ordered.filter(filterAccounts)
  }, [otherAccounts, transactions, filterAccounts])

  if (activeTab === 'mine' && myOwnOrderedAccounts.length === 0 && otherOrderedAccounts.length > 0)
    setActiveTab('others')
  if (activeTab === 'others' && otherOrderedAccounts.length === 0 && myOwnOrderedAccounts.length > 0)
    setActiveTab('mine')

  const displayedAccount = useMemo(() => {
    return activeTab === 'mine' ? myOwnOrderedAccounts : otherOrderedAccounts
  }, [myOwnOrderedAccounts, otherOrderedAccounts, activeTab])

  // Provide the filtered accounts in display order to the parent component
  useEffect(() => {
    if (onFilteredAccountsChange) {
      // Flatten all accounts in the order they are displayed
      const allDisplayedAccounts: Account[] = []

      // First add accounts from the active tab
      allDisplayedAccounts.push(...displayedAccount)

      // Then add accounts from the other tab if there are any
      if (activeTab === 'mine' && otherOrderedAccounts.length > 0) {
        allDisplayedAccounts.push(...otherOrderedAccounts)
      } else if (activeTab === 'others' && myOwnOrderedAccounts.length > 0) {
        allDisplayedAccounts.push(...myOwnOrderedAccounts)
      }

      onFilteredAccountsChange(allDisplayedAccounts)
    }
  }, [onFilteredAccountsChange, displayedAccount, myOwnOrderedAccounts, otherOrderedAccounts, activeTab])

  const segments = useMemo(() => {
    if (myOwnAccounts.length === 0 || otherAccounts.length === 0) return undefined

    return (
      <Tabs
        variant="fullWidth"
        value={activeTab}
        onChange={(_ev, value) => {
          if (value === 'mine') {
            setActiveTab('mine')
          } else if (value === 'others') {
            setActiveTab('others')
          }
        }}
      >
        <Tab
          label={
            <Stack direction="row" spacing={1}>
              <Typography>My Accounts</Typography>
              {filterable && filter && <Chip label={myOwnOrderedAccounts.length} color="primary" size="small" />}
            </Stack>
          }
          value="mine"
        />
        <Tab
          label={
            <Stack direction="row" spacing={1}>
              <Typography>Second Parties</Typography>
              {filterable && filter && <Chip label={otherOrderedAccounts.length} color="primary" size="small" />}
            </Stack>
          }
          value="others"
        />
      </Tabs>
    )
  }, [myOwnAccounts, otherAccounts, activeTab, myOwnOrderedAccounts, otherOrderedAccounts, filterable, filter])

  const groups = Object.entries(
    displayedAccount.reduce(
      (groups, account) => {
        const groupKey = (groupBy === 'type' ? account.type : account.financialInstitution) || groupOther
        return { ...groups, [groupKey]: [...(groups[groupKey] || []), account] }
      },
      {} as Record<string, Account[]>,
    ),
  )

  const getTotal = (accounts: Account[]) => {
    const now = new Date()
    const total = accounts.reduce((sum, account) => {
      const balances = accountBalances?.get(account.id)
      if (!balances) return sum
      return Array.from(balances.entries()).reduce((acc, [currencyId, amount]) => {
        if (currencyId === defaultCurrency.id) return acc + amount
        return acc + amount * exchangeRateOnDay(currencyId, defaultCurrency.id, now)
      }, sum)
    }, 0)
    return Math.round(total)
  }

  return (
    <Column style={{ position: 'relative', height: '100%' }}>
      {inlineSearch && (
        <TextField
          size="small"
          fullWidth
          placeholder="Search accounts..."
          value={inlineFilter}
          onChange={(ev) => setInlineFilter(ev.target.value)}
          sx={{ mb: 1 }}
        />
      )}
      {segments}

      <CustomScrolling ref={scrollingContainerRef} sx={{ pt: 2, pb: 11 }}>
        {groups.map(([type, accounts]) => (
          <Column key={`account-group-${type}`} sx={{ mb: { xs: 3, sm: 4 }, gap: { xs: 1, sm: 1.5 } }}>
            {type !== groupOther && (
              <GroupHeader
                title={type}
                total={showGroupTotals ? formatFull(defaultCurrency, getTotal(accounts), privacyMode) : undefined}
              />
            )}

            <Box
              sx={
                additionalItemsProps.showBalances
                  ? {
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
                      gap: { xs: 1, sm: 2 },
                      p: 0.5,
                    }
                  : { display: 'flex', flexDirection: 'column', gap: 0.25 }
              }
            >
              <ItemList
                items={accounts}
                ItemComponent={ItemComponent}
                additionalItemsProps={additionalItemsProps}
                selectConfiguration={selectConfiguration}
                filter={filterAccounts}
              />
            </Box>
          </Column>
        ))}
      </CustomScrolling>
    </Column>
  )
}

export default GroupedAccountList
